import { UntypedFormControl, NG_VALUE_ACCESSOR, ControlValueAccessor, NG_VALIDATORS, Validators, NgControl } from '@angular/forms';
import { BehaviorSubject, Subject, merge } from 'rxjs';
import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, forwardRef, Injector, Self, Optional } from '@angular/core';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { filter, startWith } from 'rxjs/operators';

@Component({
  selector: 'searchable-autocomplete',
  templateUrl: './searchable-autocomplete.component.html',
  styleUrls: ['./searchable-autocomplete.component.scss']
})
export class SearchableAutocompleteComponent implements OnInit, ControlValueAccessor {

  /**
   * Input HTML Element reference
   *
   * @private
   * @type {ElementRef}
   * @memberof SearchableAutocompleteComponent
   */
  @ViewChild('input', { static: true }) private readonly input: ElementRef;

  /**
   * List of options that will be displayed by the autocomplete
   *
   * @private
   * @type {any[]}
   * @memberof SearchableAutocompleteComponent
   */
  @Input() private readonly options: any[];

  /**
   * What field should the options be filtered by with text input
   *
   * @type {string}
   * @memberof SearchableAutocompleteComponent
   */
  @Input() filterBy: string;

  /**
   * What field should be displayed in the dropdown options as well as text box
   *
   * @type {string}
   * @memberof SearchableAutocompleteComponent
   */
  @Input() display: string;

  /**
   * Label that will be displayed inside text box when empty
   *
   * @type {string}
   * @memberof SearchableAutocompleteComponent
   */
  @Input() label: string;

  /**
   * Should the filter care about case when filtering
   *
   * @type {boolean}
   * @memberof SearchableAutocompleteComponent
   */
  @Input() ignoreCase = true;

  /**
  * Should add an empty selection field
  *
  * @type {boolean}
  * @memberof SearchableAutocompleteComponent
  */
  @Input() emptySelection = false;

  private _disabled: boolean;

  /**
   * Disable or enable the text input
   *
   * @memberof SearchableAutocompleteComponent
   */
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    value ? this.inputForm.disable() : this.inputForm.enable();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  /**
   * When updating form controls, specify member of object to be set
   *
   * @type {string}
   * @memberof SearchableAutocompleteComponent
   */
  @Input() value: string;

  @Input() exactMatch = true;

  /** 
   * Set to true of input should be number only
   * 
   * @type {boolean}
   * @memberof SearchableAutocompleteComponent
  */
  @Input() numberInput = false;

  /**
   * Set to true if the options list is made of string values
   * Will set values directly as string
   *
   * @memberof SearchableAutocompleteComponent
   */
  @Input() stringOptions = false;

  /**
   * Currently selected option from dropdown
   *
   * @private
   * @type {*}
   * @memberof SearchableAutocompleteComponent
   */
  private _selected: any = null;

  /**
   * Get the currently selected option
   *
   * @readonly
   * @memberof SearchableAutocompleteComponent
   */
  @Input()
  get selected() {
    return this._selected;
  }

  /**
   * Set the currently selected option and update dropdown
   *
   * @memberof SearchableAutocompleteComponent
   */
  set selected(val: any) {
    this._selected = val;

    if (val && typeof val !== 'string' && !this.numberInput) {
      val = this.displayFn(val);
    }

    this.inputForm.setValue(val);
    this.input.nativeElement.value = val || '';
  }

  /**
   * The selected option from the autocomplete, will be null for no selection
   *
   * @type {EventEmitter<any>}
   * @memberof SearchableAutocompleteComponent
   */
  @Output() selectedChange: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Event called if the value of the compontent changes
   * Use [(selected)] to bind to value
   * 
   * @type {EventEmitter<any>}
   * @memberof SearchableAutocompleteComponent
   */
  @Output() select: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Form used to track changes in text box
   *
   * @type {FormControl}
   * @memberof SearchableAutocompleteComponent
   */
  inputForm: UntypedFormControl = new UntypedFormControl('');

  /**
   * List of all options after being filtered
   *
   * @type {BehaviorSubject<any[]>}
   * @memberof SearchableAutocompleteComponent
   */
  filteredOptions$: BehaviorSubject<any[]> = new BehaviorSubject([]);

  private onChange = (val: any) => { };

  /**
   * Custom filter for list of options
   *
   * @private
   * @memberof SearchableAutocompleteComponent
   */
  @Input() private readonly filter: (text: string, option: any, index: number, array: any[]) => boolean = () => true;

  constructor(@Self() @Optional() private readonly control: NgControl) {
    if (control !== null) {
      this.control.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    // On Value changes for text input
    this.inputForm.valueChanges
      .pipe(
        // Start with empty string for immediate options
        startWith(''),
        // Only allow new string inputs through
        filter((val: any) => typeof val === 'string'),
        filter((text: string) => text !== this.selected)
      ).subscribe((text: string) => {
        this.updateFormValid();
        this.refreshFilter(text);
      });
  }

  private matches(l = '', r = ''): boolean {
    return this.ignoreCase ?
      l.toUpperCase() === r.toUpperCase() :
      l === r;
  }

  onFocus(event: any) {
    this.refreshFilter('');
  }

  handleChange() {
    this.onChange(this.selected && this.value ? this.selected[this.value] : this.selected);
    this.selectedChange.emit(this.selected && this.value ? this.selected[this.value] : this.selected);
    this.select.emit();
  }

  onBlur(event: any) {
    const filtered = this.filteredOptions$.value;

    if (this.numberInput) {
      if (event.target.value === '' || event.target.value == this.selected) {
        this.input.nativeElement.value = this.selected;
        return;
      }
      this.selected = event.target.value;
    } else if (event.target.value === '') {
      this.selected = null;
    } else if (this.selected && this.selected[this.display || this.filterBy] === event.target.value) {
      return;
    } else if (filtered && filtered[0] && this.matches(filtered[0][this.display || this.filterBy], event.target.value)) {
      this.selected = filtered[0];
      this.refreshFilter('');
    } else if (this.exactMatch) {
      this.input.nativeElement.value = this.displayFn(this.selected);
      return;
    } else if (this.stringOptions) {
      this.selected = event.target.value;
    } else {
      this.selected = { [this.display || this.filterBy]: event.target.value };
    }

    this.handleChange();

    this.updateFormValid();
  }

  updateFormValid() {
    this.inputForm.setErrors(this.valid ? null : { invalid: 'Invalid Selection' });
  }

  get valid() {
    return this.control ? this.control.valid : true;
  }

  writeValue(val: any): void {
    this.selected = val;
    this.selectedChange.emit(this.selected);
    this.select.emit();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(): void { }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  refreshFilter(text: string) {

    // Filter input based on custom filter 'this.filter' and text input
    const filteredOptions = (this.options || [])
      .filter((option: any, index: number, array: any[]) => this.filter(text, option, index, array))
      .filter(option => this.filterOption(text, option));

    this.filteredOptions$.next(filteredOptions);
  }

  /**
   * Filter an option based on it's 'this.filterBy' field
   * Returns true if the text shows up in the option anywhere
   * 
   * @param {string} text
   * @param {*} option
   * @returns
   * @memberof SearchableAutocompleteComponent
   */
  filterOption(text: string, option: any): boolean {

    if (option === null) {
      return false;
    }

    if (this.numberInput) {
      option = option.toString();
    }

    // If option isn't a string, get it's 'this.filterBy' column
    option = typeof option !== 'string' ? option[this.filterBy] : option;

    // If option is still not a string, it can not be filtered so return false
    if (typeof option !== 'string' || !option) {
      return false;
    }

    // If ignoring case make everything uppercase
    if (this.ignoreCase) {
      text = text.toUpperCase();
      option = option.toUpperCase();
    }

    return option.indexOf(text) !== -1;
  }

  /**
   * Called whenever mat-autocomplete has a new option selection
   *
   * @param {MatAutocompleteSelectedEvent} event
   * @memberof SearchableAutocompleteComponent
   */
  optionSelected(event: MatAutocompleteSelectedEvent): void {
    const val = event.option.value;
    if (val) {
      this.selected = val;
      this.selectedChange.emit(this.selected && this.value ? this.selected[this.value] : this.selected);
      this.select.emit();
      this.onChange(this.selected && this.value ? this.selected[this.value] : this.selected);

      this.updateFormValid();
    }
  }

  /**
   * Generates string that will be displayed in text box on a selection
   *
   * @param {*} val
   * @returns {string}
   * @memberof SearchableAutocompleteComponent
   */
  displayFn(val: any): string {
    if (!val) {
      return null;
    }

    val = (this.display || this.filterBy) ? (val[this.display || this.filterBy] || val) : val;
    return typeof val === 'string' ? val : null;
  }


  clearSelection() {
    this.selected = null;
  }
}
